import type { UserPreferences } from '@cadencia/contracts';
import { useLiveQuery } from 'dexie-react-hooks';
import { getSyncState } from './db.js';
import { getPreferences } from './repositories/profileRepo.js';

/**
 * ¿Sabemos ya lo que el usuario tiene configurado, o solo lo estamos suponiendo?
 *
 * EL PROBLEMA QUE RESUELVE (NEW5-002)
 *
 * `user_preferences` no existe hasta que alguien la escribe: un usuario que
 * nunca ha tocado nada NO tiene fila, y el valor que corresponde entonces es
 * `ai_enabled = true`, que es el `default` de la columna y lo que asume la Edge
 * Function. Hasta aquí, bien.
 *
 * El fallo estaba en que un dispositivo **recién estrenado** —Dexie vacía,
 * primera bajada todavía en vuelo— es indistinguible de ese caso mirando solo a
 * Dexie: en los dos «no hay fila». Y no son lo mismo. En el segundo la fila
 * puede existir en el servidor diciendo lo contrario, y hasta que baje, la
 * aplicación estaba afirmando «Encendida» sobre algo que no había mirado.
 *
 * Eso tenía dos consecuencias, y ninguna es cosmética:
 *
 * 1. `useCoachAI` devolvía un proveedor ENCENDIDO durante esa ventana, en un
 *    dispositivo cuyo dueño había apagado la IA. Una llamada que cuesta dinero
 *    y que contradice un ajuste explícito (§6). La ventana es de milisegundos
 *    contra el stack local y de segundos contra una red móvil, que es
 *    justamente cuando alguien reinstala y va a curiosear los ajustes.
 *
 * 2. La pantalla dejaba pulsar el interruptor mientras enseñaba esa suposición.
 *    Si la bajada llegaba entre que el usuario mira y pulsa, la casilla ya se
 *    había apagado sola y el clic la **encendía**. Comprobado, no deducido: una
 *    sonda en el manejador registró nueve escrituras de `aiEnabled: true` con
 *    el interruptor ya en `false`, y hubo exactamente nueve fallos.
 *
 * LA REGLA, EN UN SOLO SITIO
 *
 * Se sabe lo que hay configurado si **hay fila** —da igual de dónde salga, es
 * un dato real— o si **ha terminado alguna bajada** y aun así no hay ninguna,
 * porque entonces «no hay fila» ya significa lo que dice.
 *
 * Fíjate en que la primera mitad es la que mantiene esto compatible con el
 * offline-first del §5: un dispositivo que ya sincronizó alguna vez tiene fila
 * y funciona entero sin red. El único que se queda esperando es el que nunca ha
 * hablado con el servidor, y en ese la IA no podría funcionar de todas formas.
 *
 * Las dos lecturas van en la MISMA consulta a propósito. En dos consultas
 * distintas, React puede pintar un fotograma con «ya sé» de una y el valor
 * viejo de la otra, que es la misma carrera con menos milisegundos.
 */
export interface EstadoPreferencias {
  /** La fila guardada, o `null` si no hay ninguna. */
  fila: UserPreferences | null;
  /** `false` mientras «no hay fila» pueda significar «no la he mirado». */
  conocida: boolean;
}

export async function leerPreferencias(userId: string | null): Promise<EstadoPreferencias> {
  if (userId === null) return { fila: null, conocida: false };

  const fila = (await getPreferences(userId)) ?? null;
  if (fila !== null) return { fila, conocida: true };

  const estado = await getSyncState();
  return { fila: null, conocida: (estado?.lastPullAt ?? null) !== null };
}

/**
 * Lo mismo, en vivo. `undefined` mientras la consulta no ha vuelto: quien lo
 * use tiene que distinguir eso de un resultado, igual que antes.
 */
export function usePreferencias(userId: string | null): EstadoPreferencias | undefined {
  return useLiveQuery(async () => leerPreferencias(userId), [userId]);
}

/**
 * Si la IA está encendida.
 *
 * Mientras no se sepa, **apagada**. Es la única respuesta segura: encender por
 * defecto lo que no se ha mirado gasta dinero contra un ajuste que puede decir
 * lo contrario, y apagarla de más solo retrasa una redacción unos milisegundos.
 */
export function iaHabilitada(estado: EstadoPreferencias | undefined): boolean {
  if (estado === undefined || !estado.conocida) return false;
  return estado.fila?.aiEnabled ?? true;
}
